import { useCallback, useMemo, useState } from "react"
import { useSetores as useSetoresRaw } from "./useSetores"

const defaultFieldState = {
  value: "",
  inputValue: "",
  invalid: false,
  pendingResolution: false,
}

const normalizeValue = (val) => `${val || ""}`.trim()

export const useSetoresAutocompleteController = (reloadKey = 0) => {
  const optionsRaw = useSetoresRaw(reloadKey)
  const [stateMap, setStateMap] = useState({})

  // --- Helpers ---
  const getState = useCallback(
    (key) => stateMap[key] || defaultFieldState,
    [stateMap]
  )

  const findOption = useCallback(
    (rawVal) => {
      const target = normalizeValue(rawVal)
      return optionsRaw.find(opt => normalizeValue(opt) === target) || null
    },
    [optionsRaw]
  )

  const updateState = useCallback((key, updater) => {
    setStateMap(prev => {
      const current = prev[key] || defaultFieldState
      const next = updater(current)

      if (
        current.value === next.value &&
        current.inputValue === next.inputValue &&
        current.invalid === next.invalid &&
        current.pendingResolution === next.pendingResolution
      ) {
        return prev
      }

      return { ...prev, [key]: next }
    })
  }, [])

  // --- Handlers ---
  const handleChange = useCallback(
    (key, _event, newValue, reason) => {
      void _event

      updateState(key, (current) => {
        const raw =
          typeof newValue === "string"
            ? newValue
            : newValue?.value ?? newValue

        const normalized = normalizeValue(raw)

        if (reason === "clear" || (!normalized && !newValue)) {
          return defaultFieldState
        }

        const matched = findOption(normalized)
        const labelText = matched || raw || normalized

        if (reason === "selectOption") {
          return {
            value: normalized,
            inputValue: labelText,
            invalid: false,
            pendingResolution: false,
          }
        }

        return {
          value: normalized,
          inputValue: labelText,
          invalid: current.invalid,
          pendingResolution: false,
        }
      })
    },
    [findOption, updateState]
  )

  const handleInputChange = useCallback(
    (key, _event, newInput, reason) => {
      if (reason !== "input") return

      updateState(key, () => {
        if (!newInput?.trim()) return defaultFieldState

        return {
          value: normalizeValue(newInput),
          inputValue: newInput,
          invalid: false,
          pendingResolution: false,
        }
      })
    },
    [updateState]
  )

  const handleBlur = useCallback(
    (key) => {
      updateState(key, (current) => {
        const raw = current.inputValue || current.value || ""

        if (!raw.trim()) return defaultFieldState

        if (!optionsRaw.length) {
          return { ...current, pendingResolution: true, invalid: false }
        }

        const match = findOption(raw)

        return {
          value: normalizeValue(match || raw),
          inputValue: match || raw,
          invalid: false,
          pendingResolution: false,
        }
      })
    },
    [findOption, optionsRaw, updateState]
  )

  const syncValue = useCallback(
    (key, externalValue, defaultValue) => {
      const normalizedExternal = normalizeValue(externalValue)
      const normalizedDefault = normalizeValue(defaultValue)

      if (
        externalValue === undefined ||
        externalValue === null ||
        normalizedExternal === "" ||
        normalizedExternal === normalizedDefault
      ) {
        return
      }

      updateState(key, () => {
        const match = findOption(normalizedExternal)

        return {
          value: normalizedExternal,
          inputValue: match || externalValue,
          invalid: false,
          pendingResolution: !optionsRaw.length,
        }
      })
    },
    [findOption, optionsRaw, updateState]
  )

  // --- Computed ---
  const computed = useCallback(
    (key) => {
      const current = stateMap[key] || defaultFieldState

      return {
        hasError: Boolean(current.invalid),
        isLoading: Boolean(current.pendingResolution),
        hasValue: Boolean(current.value || current.inputValue),
        value: current.value || "",
        inputValue: current.inputValue || "",
      }
    },
    [stateMap]
  )

  // --- Actions ---
  const actions = useMemo(() => ({
    setInputValue: (key, inputValue) => {
      updateState(key, current => ({
        ...current,
        inputValue: inputValue || "",
      }))
    },
    setSelectedValue: (key, value) => {
      updateState(key, current => ({
        ...current,
        value: normalizeValue(value),
        invalid: false,
      }))
    },
    reset: () => setStateMap({}),
  }), [updateState])

  const handlers = useMemo(() => ({
    onChange: handleChange,
    onInputChange: handleInputChange,
    onBlur: handleBlur,
    syncValue,
  }), [handleBlur, handleChange, handleInputChange, syncValue])

  const meta = useMemo(() => ({
    options: optionsRaw,
    resolved: Boolean(optionsRaw.length),
    freeSolo: true,
    getOptionLabel: (opt) => opt,
    normalizeValue,
    filterOptions: (opts, params) => {
      const input = params?.inputValue?.trim()?.toLowerCase() || ""
      if (!input) return opts
      return opts.filter(opt => opt.toLowerCase().includes(input))
    },
    minCharsToFilter: 0,
    noOptionsText: () => "Nenhum setor encontrado",
    getOptionDisabled: () => false,
  }), [optionsRaw])

  return useMemo(() => ({
    state: { byKey: stateMap },
    getState,
    actions,
    handlers,
    meta,
    computed,
    cleanup: actions.reset,
  }), [stateMap, getState, actions, handlers, meta, computed])
}